import React from "react";
import { useParams, Navigate } from "react-router-dom";
import { Calendar, User, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useBlogPost } from "@/hooks/useBlogPosts";
import { Link } from "react-router-dom";
import { BannerAd } from "@/components/ads/BannerAd";
import { HTMLRenderer } from "@/components/ui/html-renderer";
import { JsonLd } from "@/components/seo/JsonLd";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return <Navigate to="/blog" replace />;
  }

  const { data: post, isLoading, error } = useBlogPost(slug);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <Button asChild>
            <Link to="/blog">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const siteUrl = window.location.origin;
  const canonicalUrl = `${siteUrl}/blog/${slug}`;

  return (
    <>
      <Helmet>
        <title>{post.meta_title || post.title} | Minor Baseball Talks Blog</title>
        <meta name="description" content={post.meta_description || post.excerpt || ""} />
        {post.meta_keywords && <meta name="keywords" content={post.meta_keywords} />}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt || ""} />
        <meta property="og:url" content={canonicalUrl} />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt || ""} />
        {post.featured_image && <meta name="twitter:image" content={post.featured_image} />}
      </Helmet>

      <JsonLd
        siteUrl={siteUrl}
        pathname={`/blog/${slug}`}
        pageTitle={post.title}
        pageDescription={post.excerpt || ""}
        blogPost={post}
        breadcrumb={[
          { name: "Home", url: "/" },
          { name: "Blog", url: "/blog" },
          { name: post.title, url: `/blog/${slug}` },
        ]}
      />

      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back to Blog Button */}
        <Button variant="ghost" asChild className="mb-8 hover:bg-muted">
          <Link to="/blog">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Link>
        </Button>

        {/* Article Header */}
        <article>
          <header className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <Badge variant="secondary" className="text-sm px-3 py-1">
                {post.category}
              </Badge>
              {post.read_time && (
                <span className="text-sm text-muted-foreground">
                  {post.read_time} min read
                </span>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-foreground">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                {post.excerpt}
              </p>
            )}

            <div className="flex items-center gap-6 text-sm text-muted-foreground pb-8 border-b">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="font-medium">{post.author_username || "Admin"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {new Date(
                    post.published_at || post.created_at
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
          </header>

          {/* Banner Ad */}
          <div className="my-8">
            <BannerAd
              id="blog-post-banner-ad"
              mobileUnitPath="/21849154601,423899568/Ad.Plus-Mobile-Blog-Post-Banner"
              desktopUnitPath="/21849154601,423899568/Ad.Plus-Desktop-Blog-Post-Banner"
            />
          </div>

          {/* Featured Image */}
          {post.featured_image && (
            <div className="my-12">
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-auto rounded-lg shadow-lg"
              />
            </div>
          )}

          {/* Article Content */}
          <div className="max-w-3xl mx-auto">
            <HTMLRenderer content={post.content} />
          </div>
        </article>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t">
          <div className="text-center">
            <Button asChild size="lg">
              <Link to="/blog">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Blog
              </Link>
            </Button>
          </div>
        </footer>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
